/**
 * `formatRelativeTime` — relative-time label for a chat session ("today" / "2d ago" / …).
 *
 * Shared by `<ChatSessionList>` (the session card's foot) and the chat surface's idle
 * state (the compact recent-sessions lines — U7 2026-07-05). Lives in its own module —
 * not exported from a component file — so react-refresh keeps its fast-refresh guarantee
 * on both consumers.
 */

import type { ChatSession } from './chatTypes';

/** Relative-time string keyed off `updatedAt` (falling back to `createdAt`). Accepts any
 *  session-shaped row — the full `ChatSession` or the slim `ChatSessionSummary` list row. */
export function formatRelativeTime(session: Pick<ChatSession, 'updatedAt' | 'createdAt'>): string {
  const ts = session.updatedAt || session.createdAt;
  if (!ts) return '';
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return '';
  const days = Math.round((Date.now() - date.getTime()) / 86_400_000);
  if (days < 1) return 'today';
  if (days < 2) return 'yesterday';
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.round(days / 7)}w ago`;
  return `${Math.round(days / 30)}mo ago`;
}
